import { useEffect, useState, useRef } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// Notification sound function with proper initialization
let audioContextInitialized = false;
let audioContext = null;

const playNotificationSound = () => {
  try {
    // Initialize audio context on first use (requires user interaction)
    if (!audioContextInitialized) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextInitialized = true;
    }
    
    // Resume audio context if suspended (required after user interaction)
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        // Ignore resume errors - user interaction may be required
      });
    }
    
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800; // Higher pitch for notification
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.log('Could not play notification sound:', error);
  }
};

export const useNotifications = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, isAuthenticated } = useAuth();
  const lastCheckRef = useRef(null);
  const lastNotificationIdsRef = useRef(new Set());
  const pollIntervalRef = useRef(null);
  const backoffTimeoutRef = useRef(null);
  const backoffDelayRef = useRef(30000); // Start with 30 seconds
  const consecutiveErrorsRef = useRef(0);

  const checkUnreadNotifications = async () => {
    if (!isAuthenticated || !user) return;

    try {
      // Fetch both count and recent notifications
      const [countResponse, notificationsResponse] = await Promise.all([
        api.get('/api/notifications/unread-count'),
        api.get('/api/notifications?limit=5&unreadOnly=true')
      ]);
      
      const newCount = countResponse.data.unreadCount || 0;
      const notifications = notificationsResponse.data?.notifications || [];
      
      // Reset backoff on success
      consecutiveErrorsRef.current = 0;
      backoffDelayRef.current = 30000; // Reset to 30 seconds
      
      // If we're in backoff mode (longer interval), reset to normal interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(() => {
          checkUnreadNotifications();
        }, 60000); // Normal 60 second interval
      }
      
      // Check for new notifications
      if (lastCheckRef.current !== null && newCount > lastCheckRef.current) {
        const newNotifications = notifications.filter(n => !lastNotificationIdsRef.current.has(n._id));
        
        // Play notification sound for new notifications
        if (newNotifications.length > 0) {
          playNotificationSound();
        }
        
        // Show specific toasts for each new notification
        newNotifications.forEach(notification => {
          let icon = '🔔';
          let message = notification.message || notification.title;
          
          switch (notification.type) {
            case 'booking_request':
              icon = '📋';
              toast.success(message, { icon, duration: 5000 });
              break;
            case 'booking_accepted':
              icon = '✅';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'booking_rejected':
              icon = '❌';
              toast.error(message, { icon, duration: 4000 });
              break;
            case 'payment_received':
              icon = '💰';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'trip_started':
              icon = '🚀';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'trip_completed':
              icon = '🎉';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'payout_requested':
              icon = '💳';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'payout_approved':
              icon = '✅';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'payout_completed':
              icon = '💰';
              toast.success(message, { icon, duration: 4000 });
              break;
            case 'payout_failed':
              icon = '❌';
              toast.error(message, { icon, duration: 4000 });
              break;
            default:
              toast(message, { icon, duration: 4000 });
          }
          
          lastNotificationIdsRef.current.add(notification._id);
        });
      }
      
      // Update last notification IDs
      notifications.forEach(n => {
        lastNotificationIdsRef.current.add(n._id);
      });
      
      // Keep only last 50 IDs to prevent memory issues
      if (lastNotificationIdsRef.current.size > 50) {
        const idsArray = Array.from(lastNotificationIdsRef.current);
        lastNotificationIdsRef.current = new Set(idsArray.slice(-50));
      }
      
      lastCheckRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      consecutiveErrorsRef.current += 1;
      
      // Handle rate limit errors (429) with exponential backoff
      if (error.response?.status === 429) {
        // Stop current polling immediately
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        // Exponential backoff: 30s, 60s, 120s, max 5 minutes
        backoffDelayRef.current = Math.min(backoffDelayRef.current * 2, 300000);
        
        if (backoffTimeoutRef.current) {
          clearTimeout(backoffTimeoutRef.current);
        }
        
        // Only log first few errors
        if (consecutiveErrorsRef.current <= 3) {
          console.warn(`Rate limited. Backing off for ${backoffDelayRef.current / 1000}s`);
        }
        
        // After backoff, restart polling with longer interval
        backoffTimeoutRef.current = setTimeout(() => {
          backoffTimeoutRef.current = null;
          // Restart with longer interval (30 seconds) after rate limit
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          pollIntervalRef.current = setInterval(() => {
            checkUnreadNotifications();
          }, 30000); // 30 seconds after rate limit
          checkUnreadNotifications(); // Immediate check
        }, backoffDelayRef.current);
        
        return;
      }
      
      // Exponential backoff for other errors
      backoffDelayRef.current = Math.min(backoffDelayRef.current * 2, 300000);
      
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
      }
      
      backoffTimeoutRef.current = setTimeout(() => {
        checkUnreadNotifications();
      }, backoffDelayRef.current);
      
      // Don't show error to user for polling failures
      if (consecutiveErrorsRef.current <= 3) {
        console.warn('Failed to fetch notification count:', error.message);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUnreadCount(0);
      lastCheckRef.current = null;
      lastNotificationIdsRef.current = new Set();
      consecutiveErrorsRef.current = 0;
      backoffDelayRef.current = 30000;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
        backoffTimeoutRef.current = null;
      }
      return;
    }

    // Initial check
    checkUnreadNotifications();

    // Poll every 60 seconds to reduce API calls and prevent rate limiting
    pollIntervalRef.current = setInterval(() => {
      checkUnreadNotifications();
    }, 60000); // 60 seconds - reduces API calls and prevents rate limiting

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  return { unreadCount };
};

